import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { SignUp } from '@clerk/clerk-react'
import { useAuth } from '@/hooks/useAuth'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { motion } from 'framer-motion'

const fade = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } }

export function RegisterPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="w-full">
      <motion.h2 {...fade} transition={{ duration: 0.5, delay: 0.1 }} className="text-2xl font-bold text-white">
        Start your free trial
      </motion.h2>
      <motion.p {...fade} transition={{ duration: 0.5, delay: 0.2 }} className="text-sm text-white/50 mb-8">
        16 AI agents, ready in 60 seconds. No credit card required.
      </motion.p>

      <motion.div {...fade} transition={{ duration: 0.5, delay: 0.3 }}>
        <SignUp
          routing="path"
          path="/auth/register"
          signInUrl="/auth/login"
          appearance={{
            elements: {
              rootBox: 'w-full',
              card: 'bg-transparent shadow-none p-0 w-full',
              headerTitle: 'hidden',
              headerSubtitle: 'hidden',
              socialButtonsBlockButton: 'bg-white/[0.06] border-white/[0.08] text-white hover:bg-white/[0.1]',
              formFieldInput: 'bg-white/[0.06] border-white/[0.08] text-white placeholder:text-white/30',
              formFieldLabel: 'text-white/60',
              formButtonPrimary: 'bg-primary-400 text-dark-500 hover:bg-primary-300',
              footerActionLink: 'text-white/60 hover:text-white',
              identityPreviewEditButton: 'text-white/60',
              formFieldAction: 'text-white/60',
              dividerLine: 'bg-white/10',
              dividerText: 'text-white/30',
              footer: 'hidden',
            },
          }}
        />
      </motion.div>

      {/* Value props */}
      <motion.p
        {...fade}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-5 text-center text-xs text-white/30"
      >
        14-day free trial &middot; Cancel anytime &middot; No credit card
      </motion.p>

      {/* Spacer + sign in link */}
      <div className="mt-10 text-sm text-center text-white/40">
        Already have an account?{' '}
        <Link to="/auth/login" className="text-white/60 hover:text-white transition-colors">
          Sign in
        </Link>
      </div>

      {/* Terms */}
      <p className="mt-8 text-xs text-center text-white/30">
        By signing up, you agree to our{' '}
        <Link to="/terms" className="text-white/40 hover:text-white/60 transition-colors">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="text-white/40 hover:text-white/60 transition-colors">
          Privacy Policy
        </Link>
      </p>
    </div>
  )
}
